import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, User, FileText, Activity, Upload, Calendar, Heart, Trash2, Edit3, Eye, EyeOff, Save, X, Dna, Bell, Plus, Clock, MessageCircle, Send, Loader2 } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { adminAPI } from '../../lib/api';
import { formatDate, healthConcernLabel, getRatingColor } from '../../lib/utils';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';

const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function AdminUserDetailPage() {
  const { userId } = useParams();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadData, setUploadData] = useState({
    report_type: 'gut_microbiome',
    title: '',
    report_date: '',
    file: null
  });
  const [uploading, setUploading] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: '',
    summary: '',
    specific_advice: '',
    status: 'draft'
  });
  const [saving, setSaving] = useState(false);
  
  // New state for genotypes and reminders
  const [genotypes, setGenotypes] = useState([]);
  const [userReminders, setUserReminders] = useState([]);
  const [showAddReminderDialog, setShowAddReminderDialog] = useState(false);
  const [reminderFormData, setReminderFormData] = useState({
    title: '',
    description: '',
    reminder_type: 'daily',
    time_of_day: '',
    day_of_week: 0,
    is_recurring: true,
  });
  
  // State for Report Q&A
  const [qaReport, setQaReport] = useState(null);
  const [qaQuestion, setQaQuestion] = useState('');
  const [qaHistory, setQaHistory] = useState([]);
  const [askingQuestion, setAskingQuestion] = useState(false);

  useEffect(() => {
    loadUserData();
    loadGenotypes();
  }, [userId]);

  const loadUserData = async () => {
    try {
      const [userRes, remindersRes] = await Promise.all([
        adminAPI.getUser(userId),
        adminAPI.getUserReminders(userId).catch(() => ({ data: { reminders: [] } }))
      ]);
      setUserData(userRes.data);
      setUserReminders(remindersRes.data.reminders || []);
    } catch (error) {
      console.error('Failed to load user:', error);
      toast.error('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const loadGenotypes = async () => {
    try {
      const response = await adminAPI.getGenotypes();
      setGenotypes(response.data.genotypes || []);
    } catch (error) {
      console.error('Failed to load genotypes:', error);
    }
  };

  const handleAssignGenotype = async (genotypeId) => {
    try {
      await adminAPI.assignUserGenotype(userId, genotypeId || null);
      toast.success(genotypeId ? 'Genotype assigned' : 'Genotype removed');
      loadUserData();
    } catch (error) {
      toast.error('Failed to assign genotype');
    }
  };

  const handleAddReminder = async (e) => {
    e.preventDefault();
    if (!reminderFormData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    try {
      await adminAPI.createUserReminder(userId, {
        ...reminderFormData,
        time_of_day: reminderFormData.time_of_day || null,
        day_of_week: reminderFormData.reminder_type === 'weekly' ? parseInt(reminderFormData.day_of_week) : null,
      });
      toast.success('Reminder added for user');
      setShowAddReminderDialog(false);
      setReminderFormData({
        title: '',
        description: '',
        reminder_type: 'daily',
        time_of_day: '',
        day_of_week: 0,
        is_recurring: true,
      });
      loadUserData();
    } catch (error) {
      toast.error('Failed to add reminder');
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    try {
      await adminAPI.deleteReminder(reminderId);
      toast.success('Reminder deleted');
      setUserReminders(prev => prev.filter(r => r.id !== reminderId));
    } catch (error) {
      toast.error('Failed to delete reminder');
    }
  };

  const handleUploadReport = async (e) => {
    e.preventDefault();
    if (!uploadData.file || !uploadData.title) {
      toast.error('Please fill in all fields');
      return;
    }

    setUploading(true);
    try {
      await adminAPI.uploadReport(userId, uploadData.report_type, uploadData.title, uploadData.report_date || null, uploadData.file);
      toast.success('Report uploaded successfully as draft');
      setShowUploadDialog(false);
      setUploadData({ report_type: 'gut_microbiome', title: '', report_date: '', file: null });
      loadUserData();
    } catch (error) {
      toast.error('Failed to upload report');
    } finally {
      setUploading(false);
    }
  };

  const handleEditReport = (report) => {
    setEditingReport(report);
    setEditFormData({
      title: report.title || '',
      summary: report.summary || '',
      specific_advice: report.specific_advice || '',
      status: report.status || 'draft'
    });
  };

  const handleSaveReport = async () => {
    if (!editingReport) return;
    
    setSaving(true);
    try {
      await adminAPI.updateReport(editingReport.id, editFormData);
      toast.success(editFormData.status === 'published' ? 'Report published and user notified!' : 'Report saved');
      setEditingReport(null);
      loadUserData();
    } catch (error) {
      toast.error('Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteReport = async (reportId) => {
    try {
      await adminAPI.deleteReport(reportId);
      toast.success('Report deleted');
      loadUserData();
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  const handlePublishReport = async (report) => {
    try {
      await adminAPI.updateReport(report.id, { status: 'published' });
      toast.success('Report published and user notified!');
      loadUserData();
    } catch (error) {
      toast.error('Failed to publish report');
    }
  };

  // Report Q&A handlers
  const handleOpenQA = (report) => {
    setQaReport(report);
    setQaQuestion('');
    setQaHistory([]);
  };

  const handleAskQuestion = async (e) => {
    e.preventDefault();
    if (!qaQuestion.trim() || !qaReport) return;
    
    const question = qaQuestion.trim();
    setQaHistory(prev => [...prev, { type: 'question', text: question }]);
    setQaQuestion('');
    setAskingQuestion(true);
    
    try {
      const response = await adminAPI.askAboutReport(qaReport.id, question);
      setQaHistory(prev => [...prev, { type: 'answer', text: response.data.answer }]);
    } catch (error) {
      console.error('Q&A Error:', error);
      setQaHistory(prev => [...prev, { type: 'answer', text: 'Sorry, I encountered an error processing your question. Please try again.', isError: true }]);
      toast.error('Failed to get answer');
    } finally {
      setAskingQuestion(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </AdminLayout>
    );
  }

  if (!userData) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-[#4A2C2A]/60">User not found</p>
          <Link to="/admin/users">
            <Button variant="ghost" className="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Users
            </Button>
          </Link>
        </div>
      </AdminLayout>
    );
  }

  const { user, reports, checkins } = userData;
  const profile = user?.profile;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-user-detail">
        {/* Back Link */}
        <Link to="/admin/users" className="inline-flex items-center text-[#4A2C2A]/60 hover:text-[#4A2C2A]">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Users
        </Link>

        {/* User Header */}
        <div className="soft-card p-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-[#E8A095]/20 flex items-center justify-center">
                <User className="w-8 h-8 text-[#E8A095]" />
              </div>
              <div>
                <h1 className="font-heading text-2xl text-[#4A2C2A]">{user.name}</h1>
                <p className="text-[#4A2C2A]/60">{user.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  {user.onboarding_completed ? (
                    <span className="px-2 py-1 bg-[#8DA399]/20 text-[#8DA399] rounded-full text-xs font-medium">
                      Active
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-[#E8C495]/20 text-[#E8C495] rounded-full text-xs font-medium">
                      Pending Onboarding
                    </span>
                  )}
                  <span className="text-xs text-[#4A2C2A]/50">
                    Joined {formatDate(user.created_at)}
                  </span>
                </div>
              </div>
            </div>

            <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
              <DialogTrigger asChild>
                <Button className="bg-[#4A2C2A] text-white hover:bg-[#382120] rounded-xl" data-testid="upload-report-btn">
                  <Upload className="w-4 h-4 mr-2" /> Upload Report
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">Upload Report for {user.name}</DialogTitle>
                  <DialogDescription>
                    Upload a new report. It will be saved as a draft for review before publishing.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleUploadReport} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Report Type</Label>
                    <Select
                      value={uploadData.report_type}
                      onValueChange={(value) => setUploadData(prev => ({ ...prev, report_type: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gut_microbiome">Gut Microbiome</SelectItem>
                        <SelectItem value="vaginal_microbiome">Vaginal Microbiome</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Report Title</Label>
                    <Input
                      value={uploadData.title}
                      onChange={(e) => setUploadData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Gut Microbiome Analysis - January 2025"
                      required
                      data-testid="report-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Report Date</Label>
                    <Input
                      type="date"
                      value={uploadData.report_date}
                      onChange={(e) => setUploadData(prev => ({ ...prev, report_date: e.target.value }))}
                      data-testid="report-date-input"
                    />
                    <p className="text-xs text-[#4A2C2A]/50">The date this report was created (defaults to today if not set)</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Report File</Label>
                    <Input
                      type="file"
                      accept=".pdf,.txt,.csv"
                      onChange={(e) => setUploadData(prev => ({ ...prev, file: e.target.files[0] }))}
                      required
                      data-testid="report-file-input"
                    />
                    <p className="text-xs text-[#4A2C2A]/50">Supported formats: PDF, TXT, CSV</p>
                  </div>
                  <Button
                    type="submit"
                    disabled={uploading}
                    className="w-full bg-[#4A2C2A] text-white hover:bg-[#382120]"
                    data-testid="submit-report-btn"
                  >
                    {uploading ? 'Uploading...' : 'Upload as Draft'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Profile Info */}
        {profile && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="soft-card p-6">
              <h2 className="font-heading text-lg text-[#4A2C2A] mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5 text-[#E8A095]" /> Health Focus
              </h2>
              {profile.health_goals?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {profile.health_goals.map((goal) => (
                    <span 
                      key={goal}
                      className="px-3 py-1 bg-[#E8A095]/10 text-[#4A2C2A] rounded-full text-sm"
                    >
                      {healthConcernLabel(goal)}
                    </span>
                  ))}
                </div>
              ) : profile.health_concerns?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {profile.health_concerns.map((concern) => (
                    <span 
                      key={concern}
                      className="px-3 py-1 bg-[#E8A095]/10 text-[#4A2C2A] rounded-full text-sm"
                    >
                      {healthConcernLabel(concern)}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-[#4A2C2A]/50">No health goals specified</p>
              )}
              {profile.other_goals && (
                <div className="mt-3 pt-3 border-t border-[#E8A095]/10">
                  <p className="text-sm text-[#4A2C2A]/70"><strong>Other:</strong> {profile.other_goals}</p>
                </div>
              )}
            </div>

            <div className="soft-card p-6">
              <h2 className="font-heading text-lg text-[#4A2C2A] mb-4">Personal Info</h2>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-[#4A2C2A]/50">Age</span>
                  <p className="font-medium text-[#4A2C2A]">{profile.age || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-[#4A2C2A]/50">Sex</span>
                  <p className="font-medium text-[#4A2C2A] capitalize">{profile.sex?.replace(/_/g, ' ') || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-[#4A2C2A]/50">Height</span>
                  <p className="font-medium text-[#4A2C2A]">{profile.height_cm ? `${profile.height_cm} cm` : 'N/A'}</p>
                </div>
                <div>
                  <span className="text-[#4A2C2A]/50">Weight</span>
                  <p className="font-medium text-[#4A2C2A]">{profile.weight_kg ? `${profile.weight_kg} kg` : 'N/A'}</p>
                </div>
                <div>
                  <span className="text-[#4A2C2A]/50">Blood Type</span>
                  <p className="font-medium text-[#4A2C2A]">{profile.blood_type || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Genotype Assignment */}
        <div className="soft-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg text-[#4A2C2A] flex items-center gap-2">
              <Dna className="w-5 h-5 text-[#8DA399]" /> Genotype
            </h2>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <Select
              value={user.genotype_id || 'none'}
              onValueChange={(value) => handleAssignGenotype(value === 'none' ? null : value)}
            >
              <SelectTrigger className="w-full sm:w-64" data-testid="genotype-select">
                <SelectValue placeholder="Select genotype..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No genotype assigned</SelectItem>
                {genotypes.map(g => (
                  <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {user.genotype_id && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => handleAssignGenotype(null)}
                className="text-red-500 hover:text-red-700"
              >
                Remove
              </Button>
            )}
            {genotypes.length === 0 && (
              <Link to="/admin/genotypes">
                <Button variant="outline" size="sm">
                  Create Genotypes
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* User Reminders */}
        <div className="soft-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg text-[#4A2C2A] flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#E8C495]" /> Reminders ({userReminders.length})
            </h2>
            <Dialog open={showAddReminderDialog} onOpenChange={setShowAddReminderDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="add-user-reminder-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Reminder
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">Add Reminder for {user.name}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddReminder} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Title *</Label>
                    <Input
                      value={reminderFormData.title}
                      onChange={(e) => setReminderFormData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Take morning supplements"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description (optional)</Label>
                    <Textarea
                      value={reminderFormData.description}
                      onChange={(e) => setReminderFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Additional details..."
                      rows={2}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Frequency</Label>
                      <Select
                        value={reminderFormData.reminder_type}
                        onValueChange={(value) => setReminderFormData(prev => ({ ...prev, reminder_type: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="one_time">One-time</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Time (optional)</Label>
                      <Input
                        type="time"
                        value={reminderFormData.time_of_day}
                        onChange={(e) => setReminderFormData(prev => ({ ...prev, time_of_day: e.target.value }))}
                      />
                    </div>
                  </div>
                  {reminderFormData.reminder_type === 'weekly' && (
                    <div className="space-y-2">
                      <Label>Day of Week</Label>
                      <Select
                        value={reminderFormData.day_of_week.toString()}
                        onValueChange={(value) => setReminderFormData(prev => ({ ...prev, day_of_week: parseInt(value) }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {dayNames.map((day, i) => (
                            <SelectItem key={i} value={i.toString()}>{day}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <Button type="submit" className="w-full bg-[#4A2C2A] text-white hover:bg-[#382120]">
                    Add Reminder
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          
          {userReminders.length > 0 ? (
            <div className="space-y-2">
              {userReminders.map(reminder => (
                <div key={reminder.id} className="flex items-center justify-between p-3 bg-[#FDFBF7] rounded-xl">
                  <div className="flex items-center gap-3">
                    <Bell className="w-4 h-4 text-[#E8C495]" />
                    <div>
                      <p className="font-medium text-[#4A2C2A] text-sm">{reminder.title}</p>
                      <div className="flex items-center gap-2 text-xs text-[#4A2C2A]/50">
                        <span className="capitalize">{reminder.reminder_type}</span>
                        {reminder.time_of_day && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {reminder.time_of_day}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Reminder?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently delete this reminder for the user.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => handleDeleteReminder(reminder.id)}
                          className="bg-red-500 hover:bg-red-600"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#4A2C2A]/50 text-center py-4">No reminders set for this user</p>
          )}
        </div>

        {/* Reports */}
        <div className="soft-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg text-[#4A2C2A] flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#8DA399]" /> Reports ({reports?.length || 0})
            </h2>
          </div>
          
          {reports?.length > 0 ? (
            <div className="space-y-3">
              {reports.map((report) => (
                <div 
                  key={report.id}
                  className="flex items-center justify-between p-4 bg-[#FDFBF7] rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#E8A095]/20 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-[#E8A095]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#4A2C2A]">{report.title}</p>
                      <p className="text-sm text-[#4A2C2A]/50">
                        {report.report_type?.replace(/_/g, ' ')} • {formatDate(report.report_date || report.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Status Badge */}
                    {report.status === 'published' ? (
                      <span className="flex items-center gap-1 px-2 py-1 bg-[#8DA399]/20 text-[#8DA399] rounded-full text-xs font-medium">
                        <Eye className="w-3 h-3" /> Published
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 px-2 py-1 bg-[#E8C495]/20 text-[#E8C495] rounded-full text-xs font-medium">
                        <EyeOff className="w-3 h-3" /> Draft
                      </span>
                    )}
                    
                    {/* Publish Button (only for drafts) */}
                    {report.status !== 'published' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handlePublishReport(report)}
                        className="text-[#8DA399] border-[#8DA399]/30 hover:bg-[#8DA399]/10"
                        data-testid={`publish-report-${report.id}`}
                      >
                        <Eye className="w-3 h-3 mr-1" /> Publish
                      </Button>
                    )}
                    
                    {/* Ask AI Button */}
                    <Dialog open={qaReport?.id === report.id} onOpenChange={(open) => !open && setQaReport(null)}>
                      <DialogTrigger asChild>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleOpenQA(report)}
                          className="text-[#8DA399] hover:text-[#8DA399]/80"
                          data-testid={`ask-report-${report.id}`}
                        >
                          <MessageCircle className="w-4 h-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl max-h-[85vh]">
                        <DialogHeader>
                          <DialogTitle className="font-heading text-xl flex items-center gap-2">
                            <MessageCircle className="w-5 h-5 text-[#8DA399]" />
                            Ask About Report
                          </DialogTitle>
                          <DialogDescription>
                            Ask questions about "{report.title}" and get AI-powered insights
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          {/* Q&A History */}
                          <div className="bg-[#FDFBF7] rounded-xl p-4 h-[300px] overflow-y-auto space-y-3">
                            {qaHistory.length === 0 ? (
                              <div className="text-center text-[#4A2C2A]/50 py-8">
                                <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                <p className="text-sm">Ask a question about this report</p>
                                <p className="text-xs mt-2">Examples:</p>
                                <div className="flex flex-wrap justify-center gap-2 mt-2">
                                  {['What are the main concerns?', 'Any dietary recommendations?', 'What should be prioritized?'].map((q, i) => (
                                    <button
                                      key={i}
                                      onClick={() => setQaQuestion(q)}
                                      className="text-xs px-2 py-1 bg-[#E8A095]/10 text-[#4A2C2A] rounded-full hover:bg-[#E8A095]/20 transition-colors"
                                    >
                                      {q}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              qaHistory.map((item, idx) => (
                                <div 
                                  key={idx} 
                                  className={`p-3 rounded-lg ${
                                    item.type === 'question' 
                                      ? 'bg-[#4A2C2A] text-white ml-8' 
                                      : item.isError 
                                        ? 'bg-red-50 text-red-700 mr-8' 
                                        : 'bg-white border border-[#E8A095]/20 mr-8'
                                  }`}
                                >
                                  <p className="text-sm whitespace-pre-wrap">{item.text}</p>
                                </div>
                              ))
                            )}
                            {askingQuestion && (
                              <div className="flex items-center gap-2 text-[#4A2C2A]/50 p-3">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span className="text-sm">Analyzing report...</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Question Input */}
                          <form onSubmit={handleAskQuestion} className="flex gap-2">
                            <Input
                              value={qaQuestion}
                              onChange={(e) => setQaQuestion(e.target.value)}
                              placeholder="Ask a question about this report..."
                              disabled={askingQuestion}
                              className="flex-1"
                              data-testid="qa-question-input"
                            />
                            <Button 
                              type="submit" 
                              disabled={askingQuestion || !qaQuestion.trim()}
                              className="bg-[#4A2C2A] text-white hover:bg-[#382120]"
                              data-testid="qa-submit-btn"
                            >
                              {askingQuestion ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            </Button>
                          </form>
                        </div>
                      </DialogContent>
                    </Dialog>
                    
                    {/* Edit Button */}
                    <Dialog open={editingReport?.id === report.id} onOpenChange={(open) => !open && setEditingReport(null)}>
                      <DialogTrigger asChild>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleEditReport(report)}
                          data-testid={`edit-report-${report.id}`}
                        >
                          <Edit3 className="w-4 h-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle className="font-heading text-xl">Edit Report</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                          <div className="space-y-2">
                            <Label>Title</Label>
                            <Input
                              value={editFormData.title}
                              onChange={(e) => setEditFormData(prev => ({ ...prev, title: e.target.value }))}
                              data-testid="edit-report-title"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Summary</Label>
                            <Textarea
                              value={editFormData.summary}
                              onChange={(e) => setEditFormData(prev => ({ ...prev, summary: e.target.value }))}
                              rows={4}
                              data-testid="edit-report-summary"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Specific Advice (Optional)</Label>
                            <Textarea
                              value={editFormData.specific_advice}
                              onChange={(e) => setEditFormData(prev => ({ ...prev, specific_advice: e.target.value }))}
                              rows={4}
                              placeholder="Add any specific advice for this user..."
                              data-testid="edit-report-advice"
                            />
                            <p className="text-xs text-[#4A2C2A]/50">This will only appear if you add content here.</p>
                          </div>
                          <div className="space-y-2">
                            <Label>Status</Label>
                            <Select
                              value={editFormData.status}
                              onValueChange={(value) => setEditFormData(prev => ({ ...prev, status: value }))}
                            >
                              <SelectTrigger data-testid="edit-report-status">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="draft">Draft (Not visible to user)</SelectItem>
                                <SelectItem value="published">Published (Visible to user)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {/* Analysis Preview */}
                          {report.analysis && (
                            <div className="bg-[#FDFBF7] p-4 rounded-xl space-y-3">
                              <h4 className="font-medium text-[#4A2C2A]">AI Analysis</h4>
                              {report.analysis.key_findings?.length > 0 && (
                                <div>
                                  <p className="text-sm text-[#4A2C2A]/60 mb-1">Key Findings:</p>
                                  <ul className="list-disc list-inside text-sm text-[#4A2C2A]/80">
                                    {report.analysis.key_findings.map((f, i) => (
                                      <li key={i}>{f}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {report.analysis.recommendations?.length > 0 && (
                                <div>
                                  <p className="text-sm text-[#4A2C2A]/60 mb-1">Recommendations:</p>
                                  <ul className="list-disc list-inside text-sm text-[#4A2C2A]/80">
                                    {report.analysis.recommendations.map((r, i) => (
                                      <li key={i}>{r}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex justify-end gap-2 pt-4 border-t">
                          <Button variant="outline" onClick={() => setEditingReport(null)}>
                            Cancel
                          </Button>
                          <Button 
                            onClick={handleSaveReport} 
                            disabled={saving}
                            className="bg-[#4A2C2A] text-white hover:bg-[#382120]"
                            data-testid="save-report-btn"
                          >
                            {saving ? 'Saving...' : 'Save Changes'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                    
                    {/* Delete Button */}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-report-${report.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Report?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete "{report.title}". This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDeleteReport(report.id)}
                            className="bg-red-500 hover:bg-red-600"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#4A2C2A]/50 text-center py-6">No reports uploaded yet</p>
          )}
        </div>

        {/* Recent Check-ins */}
        <div className="soft-card p-6">
          <h2 className="font-heading text-lg text-[#4A2C2A] mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-[#E8C495]" /> Recent Check-ins ({checkins?.length || 0})
          </h2>
          
          {checkins?.length > 0 ? (
            <div className="space-y-3">
              {checkins.slice(0, 5).map((checkin) => (
                <div 
                  key={checkin.id}
                  className="p-4 bg-[#FDFBF7] rounded-xl"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-[#4A2C2A]/60">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      {formatDate(checkin.created_at)}
                    </span>
                  </div>
                  <div className="flex gap-4">
                    {checkin.responses?.map((r) => (
                      <div key={r.question_id} className="text-center">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-1"
                          style={{ backgroundColor: getRatingColor(r.rating) + '20' }}
                        >
                          <span className="font-medium" style={{ color: getRatingColor(r.rating) }}>
                            {r.rating}
                          </span>
                        </div>
                        <span className="text-xs text-[#4A2C2A]/50 capitalize">{r.category}</span>
                      </div>
                    ))}
                  </div>
                  {checkin.notes && (
                    <p className="mt-3 text-sm text-[#4A2C2A]/70 italic">"{checkin.notes}"</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#4A2C2A]/50 text-center py-6">No check-ins recorded</p>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
